import React, { useState } from "react";
import {
  AVATAR_GRADIENT,
  EMAIL_RE,
  ErrorBanner,
  FONT,
  InfoBanner,
  NAME_MAX,
  Spinner,
  fieldBox,
  fieldHeight,
  h2Style,
  inputStyle,
  isValidName,
  labelStyle,
  nameParts,
  primaryButton,
  rise,
  subStyle,
} from "./authFlowUi";

const CHIPS = [
  { icon: "/credit-signin.svg", label: "14-day free trial" },
  { icon: "/cancel-signin.svg", label: "No credit card" },
  { icon: "/secure-signin.svg", label: "Secure login" },
];

const COPY = {
  signup: {
    title: "Create your account",
    sub: "Start your 14-day free trial. No password, no credit card.",
    switchText: "Already have an account?",
    switchAction: "Sign in",
  },
  signin: {
    title: "Welcome back",
    sub: "Sign in with a one-time code or your Google account.",
    switchText: "New to DocuSensa?",
    switchAction: "Create an account",
  },
};

const hintStyle = (color) => ({ margin: "7px 0 0", font: `500 12px ${FONT}`, color });

const StepDetails = ({
  flow,
  mobile,
  short,
  roomy,
  showChips,
  name,
  email,
  onNameChange,
  onEmailChange,
  promoCode,
  onPromoChange,
  loading,
  error,
  notice,
  onSend,
  onSwitchFlow,
  googleButton,
}) => {
  const [nameTouched, setNameTouched] = useState(false);
  const [nameFocus, setNameFocus] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [promoOpen, setPromoOpen] = useState(!!promoCode);

  const signup = flow === "signup";
  const copy = COPY[flow];
  const H = fieldHeight(mobile, short, roomy);
  const gap = short || mobile ? 12 : 16;
  const nm = name.trim();
  const nameOk = isValidName(name);
  const { first, initials } = nameParts(name, email);
  const nameBad = nameTouched && !nameFocus && !nameOk;
  const emailOk = EMAIL_RE.test(email);
  const emailBad = !emailFocus && email.length > 0 && !emailOk;
  const canSend = (!signup || nameOk) && emailOk && !loading;

  const trySend = () => {
    if (!canSend) {
      setNameTouched(true);
      return;
    }
    onSend();
  };
  const submitOnEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      trySend();
    }
  };

  const nameHint = nameBad
    ? nm
      ? "Use letters only, no numbers or symbols."
      : "Add your name so your team knows who's who."
    : nameOk
    ? `We’ll greet you as ${first} inside DocuSensa.`
    : "Shown on your profile and to teammates.";

  return (
    <div style={rise}>
      <h2 style={{ ...h2Style, fontSize: roomy ? 30 : short ? 24 : 26 }}>{copy.title}</h2>
      {mobile ? (
        <div style={{ height: 16 }} />
      ) : (
        <p style={{ ...subStyle(roomy ? "10px 0 26px" : short ? "6px 0 16px" : "8px 0 22px"), fontSize: roomy ? 15 : 14 }}>{copy.sub}</p>
      )}

      {notice && <InfoBanner style={{ marginBottom: 14 }}>{notice}</InfoBanner>}

      {googleButton}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          color: "rgba(115,96,123,.6)",
          fontSize: 13,
          fontWeight: 500,
          margin: short ? "14px 0" : "18px 0",
        }}
      >
        <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,.10)" }} />
        <span style={{ margin: "0 14px", whiteSpace: "nowrap" }}>or use your email</span>
        <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,.10)" }} />
      </div>

      {signup && (
        <>
          <label htmlFor="ds-name" style={labelStyle}>Your name</label>
          <div style={fieldBox(nameBad ? "bad" : nameFocus ? "focus" : nameOk ? "ok" : "idle", H)}>
            <img src="/fullnameicon.svg" alt="" style={{ width: 20, height: 20, position: "absolute", left: 14, opacity: 0.8 }} />
            <input
              id="ds-name"
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value.replace(/\s{2,}/g, " "))}
              onFocus={() => setNameFocus(true)}
              onBlur={() => {
                setNameFocus(false);
                setNameTouched(true);
              }}
              onKeyDown={submitOnEnter}
              placeholder="Enter your name here"
              autoComplete="name"
              maxLength={NAME_MAX}
              style={inputStyle("0 50px 0 48px")}
            />
            {nameOk && (
              <span
                style={{
                  position: "absolute",
                  right: 10,
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: AVATAR_GRADIENT,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  font: `600 12px ${FONT}`,
                  letterSpacing: ".02em",
                  animation: "dsPop 260ms cubic-bezier(.22,1,.36,1) both",
                }}
              >
                {initials}
              </span>
            )}
          </div>
          <p style={hintStyle(nameBad ? "#E0476B" : nameOk ? "#1F9A6B" : "#9A8AA0")}>{nameHint}</p>
        </>
      )}

      <label htmlFor="ds-email" style={{ ...labelStyle, margin: signup ? `${gap}px 0 7px` : "0 0 7px" }}>Work email</label>
      <div style={fieldBox(emailBad ? "bad" : emailFocus ? "focus" : emailOk ? "ok" : "idle", H)}>
        <img src="/email-signin.svg" alt="" style={{ width: 20, height: 20, position: "absolute", left: 14 }} />
        <input
          id="ds-email"
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value.trim())}
          onFocus={() => setEmailFocus(true)}
          onBlur={() => setEmailFocus(false)}
          onKeyDown={submitOnEnter}
          placeholder="you@company.com"
          autoComplete="email"
          style={inputStyle("0 40px 0 48px", roomy)}
        />
        {emailOk && (
          <span
            style={{
              position: "absolute",
              right: 14,
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "#1F9A6B",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              animation: "dsPop 240ms cubic-bezier(.22,1,.36,1) both",
            }}
          >
            ✓
          </span>
        )}
        {emailBad && <img src="/error.svg" alt="" style={{ position: "absolute", right: 14, width: 18, height: 18 }} />}
      </div>
      {emailBad && <p style={hintStyle("#E0476B")}>That doesn't look like a complete email address.</p>}

      {signup &&
        (promoOpen ? (
          <div style={{ marginTop: gap }}>
            <label htmlFor="ds-promo" style={labelStyle}>Promo code</label>
            <div style={fieldBox(promoCode.trim() ? "filled" : "idle", H)}>
              <input
                id="ds-promo"
                type="text"
                value={promoCode}
                onChange={(e) => onPromoChange(e.target.value)}
                onKeyDown={submitOnEnter}
                placeholder="Enter your promo code"
                autoComplete="off"
                style={inputStyle("0 16px")}
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="ds-link-btn"
            onClick={() => setPromoOpen(true)}
            style={{ marginTop: 10, padding: 0, border: "none", background: "none", cursor: "pointer", font: `600 12.5px ${FONT}`, color: "#8B55DD" }}
          >
            Have a promo code?
          </button>
        ))}

      {error && <ErrorBanner style={{ marginTop: 14 }}>{error}</ErrorBanner>}

      <div style={{ marginTop: short ? 18 : 22 }}>
        <button type="button" onClick={trySend} style={primaryButton(canSend, H)}>
          {loading && <Spinner />}
          {loading ? "Sending code…" : "Send code"}
        </button>
      </div>
      <p style={{ margin: "12px 0 0", textAlign: "center", color: "#8B7D91", fontSize: 12, fontWeight: 500 }}>
        No password. We email you a 6-digit code.
      </p>
      <p style={{ margin: "10px 0 0", textAlign: "center", color: "#5C5060", fontSize: 13, fontWeight: 500 }}>
        {copy.switchText}{" "}
        <button
          type="button"
          className="ds-link-btn"
          onClick={onSwitchFlow}
          style={{ padding: 0, border: "none", background: "none", cursor: "pointer", font: `600 13px ${FONT}`, color: "#8B55DD" }}
        >
          {copy.switchAction}
        </button>
      </p>

      {signup && showChips && (
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            justifyContent: "center",
            gap: 6,
            marginTop: short ? 14 : 18,
            paddingTop: short ? 14 : 16,
            borderTop: "1px solid rgba(0,0,0,.08)",
          }}
        >
          {CHIPS.map((chip) => (
            <div
              key={chip.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,.08)",
                background: "rgba(255,255,255,.7)",
                fontSize: 11,
                fontWeight: 500,
                color: "#5C5060",
                whiteSpace: "nowrap",
              }}
            >
              <img src={chip.icon} alt="" style={{ width: 12, height: 12 }} />
              {chip.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StepDetails;
